<template>
  <div class="card p-shadow-app fleet-overview">
    <div class="fleet-header">
      <div><h1>Gestão da frota</h1><p>Telemetria · Últimas posições comunicadas pelos veículos</p></div>
      <Button label="Atualizar" icon="pi pi-refresh" class="p-button-outlined" :disabled="loading || locationsLoading" @click="loadVehicles" />
    </div>
    <Toast :style="{ width: $isMobile() ? '85vw' : '' }" />
    <Message v-if="error" severity="error" :closable="false">{{ error }}</Message>
    <Message v-if="locationsError" severity="warn" :closable="false">{{ locationsError }}</Message>
    <div class="fleet-summary">
      <KpiCard label="Veículos" :value="vehicles.length" icon="pi pi-car" />
      <KpiCard label="Posições disponíveis" :value="locationsLoading ? 'A carregar...' : locatedVehicles.length" icon="pi pi-map-marker" tone="blue" />
      <KpiCard label="Sem posição disponível" :value="locationsLoading ? 'A carregar...' : fleetVehicles.filter((vehicle) => !vehicle.position && !vehicle.lookupFailed).length" :detail="failedVins.length ? `${failedVins.length} consultas indisponíveis, não incluídas` : 'Inclui veículos em modo de privacidade'" icon="pi pi-question-circle" tone="amber" />
    </div>
    <div class="fleet-controls">
      <div class="fleet-search"><label for="fleetSearch">Pesquisar veículo</label><InputText id="fleetSearch" v-model="search" placeholder="Matrícula ou VIN" type="search" /></div>
      <div><label for="fleetLocation">Localização</label><Dropdown id="fleetLocation" v-model="locationFilter" :options="locationOptions" optionLabel="label" optionValue="value" /></div>
      <Button label="Limpar filtros" icon="pi pi-filter-slash" class="p-button-text" @click="clearFilters" />
      <div class="view-switch" aria-label="Vista da frota">
        <Button label="Mapa" icon="pi pi-map" :class="view === 'map' ? '' : 'p-button-outlined'" :aria-pressed="view === 'map'" @click="view = 'map'" />
        <Button label="Tabela" icon="pi pi-list" :class="view === 'table' ? '' : 'p-button-outlined'" :aria-pressed="view === 'table'" @click="view = 'table'" />
      </div>
    </div>
    <p class="fleet-note" role="status">{{ locationsLoading ? 'A carregar posições GPS...' : `${filteredVehicles.length} veículos nesta vista` }}<span v-if="updatedAt"> · Consulta: {{ formatTimestamp(updatedAt) }} (Lisboa)</span></p>
    <div v-if="view === 'map'" class="fleet-map-layout">
      <section class="vehicle-roster" aria-label="Veículos da frota">
        <p v-if="!filteredVehicles.length">{{ loading ? 'A carregar veículos...' : 'Não existem veículos para estes filtros.' }}</p>
        <article v-for="vehicle in filteredVehicles" :key="vehicle.vin || vehicle.plate" class="roster-item" :class="{ 'is-selected': selectedVin === vehicle.vin }">
          <div class="roster-heading"><router-link v-if="vehicle.vin" :to="vehicleRoute(vehicle)" class="clickable-item p-mb-2">{{ vehicle.plate || vehicle.vin }}</router-link><strong v-else>{{ vehicle.plate || 'Sem matrícula' }}</strong><span class="position-badge" :class="{ 'has-position': vehicle.position }">{{ positionLabel(vehicle) }}</span></div>
          <div class="fleet-association">
            <router-link v-if="localFleetRoute(vehicle)" :to="localFleetRoute(vehicle)" class="clickable-item">Ficha da frota: {{ vehicle.fleet_vehicle.registration || vehicle.plate || vehicle.vin }}</router-link>
            <span v-else>Associado à frota local</span>
            <small v-if="vehicle.fleet_vehicle">{{ vehicle.fleet_vehicle.brand }} {{ vehicle.fleet_vehicle.model }} · {{ vehicle.fleet_match === 'vin' ? 'Correspondência por VIN' : 'Correspondência por matrícula' }}</small>
          </div>
          <p>{{ vehicle.address || 'Morada não disponível' }}</p>
          <small>GPS: {{ formatTimestamp(vehicle.positionTime) }}</small>
          <div class="roster-actions"><span>{{ vehicle.speed }}</span><Button v-if="vehicle.position" label="Localizar" icon="pi pi-map-marker" class="p-button-text p-button-sm" @click="focusVehicle(vehicle)" /></div>
        </article>
      </section>
      <section class="fleet-map-panel" aria-label="Mapa das últimas posições da frota">
        <div class="map-heading"><div><h2>Onde está a frota?</h2><p>{{ visiblePositions.length }} posições disponíveis · Não é uma vista em tempo real.</p></div><Button label="Ver todos" icon="pi pi-search-minus" class="p-button-text p-button-sm" @click="fitMap" /></div>
        <div class="fleet-map">
          <LMap ref="fleetMap" :zoom="6" :center="[39.5, -8]" @ready="fitMap">
            <LTileLayer :url="tileUrl" :attribution="tileAttribution" />
            <LCircleMarker v-for="vehicle in visiblePositions" :key="vehicle.vin" :lat-lng="vehicle.position" :radius="selectedVin === vehicle.vin ? 11 : 7" :color="vehicle.positionRecent ? '#3969ac' : '#d88320'" :fillColor="vehicle.positionRecent ? '#3969ac' : '#d88320'" :fillOpacity="0.85" @click="selectedVin = vehicle.vin">
              <LPopup><router-link :to="vehicleRoute(vehicle)"><strong>{{ vehicle.plate || vehicle.vin }}</strong></router-link><br />{{ vehicle.address || 'Morada não disponível' }}<br />GPS: {{ formatTimestamp(vehicle.positionTime) }}<br />{{ vehicle.speed }}</LPopup>
            </LCircleMarker>
          </LMap>
        </div>
        <p class="fleet-note">À data da consulta: azul indica GPS nas 24 h anteriores; laranja indica posição mais antiga ou sem data válida. Veículos sem coordenadas continuam na lista. Atualize para obter novas leituras.</p>
      </section>
    </div>
    <div v-else class="fleet-table">
      <DataTable
        :value="filteredVehicles"
        :filters.sync="filters"
        filterDisplay="row"
        :loading="loading"
        class="p-datatable-sm"
        responsiveLayout="scroll"
        removableSort
        sortMode="single"
        :scrollable="true"
        scrollHeight="flex"
        :autoLayout="true"
        :paginator="true"
        :rows="20"
        :rowsPerPageOptions="[20, 50, 100]"
      >
        <template #empty>{{ error ? "Lista indisponível." : "Não existem veículos a apresentar." }}</template>
        <template #loading>A carregar. Por favor Aguarde...</template>
        <Column field="plate" filterField="plate" header="Matrícula" sortable filterMatchMode="contains" :showFilterMenu="false">
          <template #body="slotProps">
            <router-link v-if="slotProps.data.vin" class="clickable-item" :to="vehicleRoute(slotProps.data)">
              {{ slotProps.data.plate || slotProps.data.vin }}
            </router-link>
            <span v-else>{{ slotProps.data.plate || "Sem matrícula" }} (sem VIN)</span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <InputText type="text" v-model="filterModel.value" class="p-column-filter" @input="filterCallback()" />
          </template>
        </Column>
        <Column field="address" header="Última localização" />
        <Column header="Data GPS"><template #body="{ data }">{{ formatTimestamp(data.positionTime) }}</template></Column>
        <Column header="Velocidade GPS"><template #body="{ data }">{{ data.speed }}</template></Column>
        <Column header="Posição"><template #body="{ data }">{{ positionLabel(data) }}</template></Column>
        <Column header="Frota local">
          <template #body="{ data }">
            <router-link v-if="localFleetRoute(data)" :to="localFleetRoute(data)" class="clickable-item">{{ data.fleet_vehicle.registration || 'Abrir ficha da frota' }}</router-link>
            <span v-else>Associado</span>
          </template>
        </Column>
        <Column field="vin" filterField="vin" header="VIN" sortable filterMatchMode="contains" :showFilterMenu="false">
          <template #filter="{ filterModel, filterCallback }">
            <InputText type="text" v-model="filterModel.value" class="p-column-filter" @input="filterCallback()" />
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script>
import { FilterMatchMode } from "primevue/api";
import TargaTelematicsService from "../services/targaTelematics.service";
import KpiCard from "../components/dashboards/KpiCard.vue";
import { LMap, LTileLayer, LCircleMarker, LPopup } from "vue2-leaflet";
import { latLngBounds } from "leaflet";
import { coordinates, reading, formatReading, formatTimestamp } from "../helpers/targaTelematics";

export default {
  name: "TargaTelematics",
  components: { KpiCard, LMap, LTileLayer, LCircleMarker, LPopup },
  data() {
    return {
      vehicles: [],
      loading: false,
      error: "",
      twins: {}, failedVins: [], locationsLoading: false, locationsError: "", updatedAt: null,
      search: "", locationFilter: "all", view: "map", selectedVin: null,
      locationOptions: [{ label: "Todos os veículos", value: "all" }, { label: "Com posição GPS", value: "located" }, { label: "Sem posição GPS", value: "missing" }],
      tileUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      tileAttribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      requestSequence: 0,
      filters: {
        plate: { value: null, matchMode: FilterMatchMode.CONTAINS },
        vin: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
    };
  },
  computed: {
    fleetVehicles() {
      return this.vehicles.map((vehicle) => {
        const twin = this.twins[vehicle.vin];
        const gps = twin && twin.gpsData;
        const positionTime = gps && gps.coordinates && gps.coordinates.timestamp;
        const age = Date.parse(this.updatedAt) - Date.parse(positionTime);
        const privacy = reading(twin, "privacy.privacyOn").value === true;
        return { ...vehicle, position: privacy ? null : coordinates(gps && gps.coordinates), positionTime,
          positionRecent: Number.isFinite(age) && age >= 0 && age <= 86400000,
          address: twin && twin.address && twin.address.formattedAddress,
          privacy,
          lookupFailed: this.failedVins.includes(vehicle.vin),
          speed: formatReading("gpsData.speed", reading(twin, "gpsData.speed").value),
        };
      });
    },
    locatedVehicles() { return this.fleetVehicles.filter((vehicle) => vehicle.position); },
    filteredVehicles() {
      const search = this.search.trim().toLowerCase();
      return this.fleetVehicles.filter((vehicle) =>
        `${vehicle.plate || ""} ${vehicle.vin || ""}`.toLowerCase().includes(search)
        && (!this.filters.plate.value || String(vehicle.plate || "").toLowerCase().includes(this.filters.plate.value.toLowerCase()))
        && (!this.filters.vin.value || String(vehicle.vin || "").toLowerCase().includes(this.filters.vin.value.toLowerCase()))
        && (this.locationFilter === "all" || (this.locationFilter === "located" ? vehicle.position : !vehicle.position))
      );
    },
    visiblePositions() { return this.filteredVehicles.filter((vehicle) => vehicle.position); },
  },
  watch: {
    visiblePositions: "fitMap",
    view() { this.fitMap(); },
  },
  created() {
    this.loadVehicles();
  },
  beforeDestroy() {
    this.requestSequence++;
  },
  methods: {
    formatTimestamp,
    clearFilters() {
      this.search = "";
      this.locationFilter = "all";
      this.filters.plate.value = null;
      this.filters.vin.value = null;
    },
    positionLabel(vehicle) {
      if (vehicle.lookupFailed) return "Consulta indisponível";
      if (vehicle.privacy) return "Privacidade ativa";
      if (!vehicle.position) return this.locationsLoading ? "A carregar..." : "Sem posição";
      return vehicle.positionRecent ? "GPS recente na consulta" : vehicle.positionTime ? "GPS antigo / data inválida" : "GPS sem data";
    },
    fitMap() {
      this.$nextTick(() => {
        const map = this.$refs.fleetMap && this.$refs.fleetMap.mapObject;
        if (!map) return;
        map.invalidateSize({ pan: false });
        if (this.visiblePositions.length) map.fitBounds(latLngBounds(this.visiblePositions.map((vehicle) => vehicle.position)), { padding: [32, 32], maxZoom: 14 });
        else map.setView([39.5, -8], 6);
      });
    },
    focusVehicle(vehicle) {
      this.selectedVin = vehicle.vin;
      const map = this.$refs.fleetMap && this.$refs.fleetMap.mapObject;
      if (map) map.setView(vehicle.position, 15);
    },
    vehicleRoute(vehicle) {
      const plate = vehicle.plate || (vehicle.fleet_vehicle && vehicle.fleet_vehicle.registration);
      return {
        name: "targaTelematicsVehicleDetails",
        params: { vin: vehicle.vin },
        query: plate ? { plate } : {},
      };
    },
    localFleetRoute(vehicle) {
      return vehicle.fleet_vehicle && vehicle.fleet_vehicle.id && this.can("seeFleetVehicleDetails")
        ? { name: "vehicle-details", params: { id: vehicle.fleet_vehicle.id } } : null;
    },
    async loadVehicles() {
      const sequence = ++this.requestSequence;
      this.loading = true;
      this.error = "";
      this.vehicles = [];
      this.twins = {};
      this.failedVins = [];
      this.locationsError = "";
      this.locationsLoading = false;
      this.selectedVin = null;
      this.updatedAt = null;
      try {
        const vehicles = await TargaTelematicsService.getVehicles();
        if (sequence !== this.requestSequence) return;
        if (!Array.isArray(vehicles)) throw new Error("Invalid vehicle list");
        this.vehicles = vehicles.filter((vehicle) => vehicle && typeof vehicle === "object");
        const vins = [...new Set(this.vehicles.map((vehicle) => vehicle.vin).filter(Boolean))];
        this.locationsLoading = vins.length > 0;
        let failedBatches = 0;
        for (let index = 0; index < vins.length; index += 1000) {
          if (sequence !== this.requestSequence) return;
          try {
            const twins = await TargaTelematicsService.getVehiclesDigitalTwins(vins.slice(index, index + 1000));
            if (sequence !== this.requestSequence) return;
            if (!twins || typeof twins !== "object" || Array.isArray(twins)) throw new Error("Invalid Digital Twin map");
            this.twins = { ...this.twins, ...twins };
            this.updatedAt = new Date().toISOString();
          } catch (error) {
            if (sequence !== this.requestSequence) return;
            failedBatches++;
            this.failedVins.push(...vins.slice(index, index + 1000));
          }
        }
        if (failedBatches) this.locationsError = "Não foi possível consultar todas as posições GPS. A lista de veículos continua disponível; tente atualizar.";
        this.updatedAt = new Date().toISOString();
      } catch (error) {
        if (sequence !== this.requestSequence) return;
        this.error = "Não foi possível carregar os veículos. Tente atualizar.";
        this.$toast.add({ severity: "error", summary: "Telemetria", detail: this.error, life: 5000 });
      } finally {
        if (sequence === this.requestSequence) { this.loading = false; this.locationsLoading = false; }
      }
    },
  },
};
</script>

<style scoped>
.fleet-overview { min-width: 0; }
.fleet-header, .fleet-controls, .map-heading, .roster-heading, .roster-actions { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
.fleet-header h1, .map-heading h2 { margin: 0; }
.fleet-header p, .map-heading p, .fleet-note { color: #667085; }
.fleet-summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1rem; margin: 1.5rem 0; }
.fleet-controls { align-items: flex-end; }
.fleet-controls label { display: block; margin-bottom: 0.4rem; font-weight: 600; }
.fleet-search { flex: 1; }
.fleet-search input { width: 100%; }
.view-switch { display: flex; gap: 0.5rem; }
.fleet-map-layout { display: grid; grid-template-columns: minmax(280px, 1fr) minmax(0, 2fr); gap: 1rem; }
.vehicle-roster { max-height: 650px; overflow-y: auto; }
.roster-item { padding: 1rem; border: 1px solid #e4e7ec; border-radius: 10px; margin-bottom: 0.6rem; }
.roster-item.is-selected { border-color: #3969ac; background: #f3f7fc; }
.roster-item p { margin: 0.6rem 0; }
.roster-item small { color: #667085; }
.fleet-association { display: flex; flex-direction: column; gap: 0.3rem; font-size: 0.85rem; margin-top: 0.6rem; }
.roster-heading a { font-weight: 700; }
.position-badge { font-size: 0.75rem; background: #fff4e5; color: #82520e; border-radius: 6px; padding: 0.25rem 0.5rem; }
.position-badge.has-position { background: #edf3fa; color: #28558c; }
.fleet-map-panel { min-width: 0; border: 1px solid #e4e7ec; border-radius: 12px; padding: 1rem; }
.map-heading h2 { font-size: 1.25rem; }
.map-heading p { margin: 0.5rem 0; }
.fleet-map { height: 480px; position: relative; z-index: 0; }
.fleet-table { height: 65vh; }
@media (max-width: 900px) {
  .fleet-map-layout { grid-template-columns: minmax(0, 1fr); }
  .fleet-map-panel { grid-row: 1; }
  .fleet-map { height: 360px; }
  .vehicle-roster { max-height: 420px; }
}
@media (max-width: 600px) {
  .fleet-summary { grid-template-columns: minmax(0, 1fr); }
  .fleet-controls > div { width: 100%; }
  .fleet-controls :deep(.p-dropdown) { width: 100%; }
}
</style>
